import { patientGroupName, pickerPeople } from '../../core/patient-chat';
import { avatarHue, initialsOf } from '../../core/avatar';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChatService } from '../../chat.service';
import { BillingRelation, HubRequestError } from '../../core/hub-types';
import { firstValueFrom } from 'rxjs';
import { NetworkContactLink, NetworkContactPerson } from '../../../core/models/referral.model';
import { REFERRALS_ENABLED, ReferralService } from '../../../core/services/referral.service';
import { normalise } from '../../core/patient-mentions';

export interface NewConversationData {
  orgId: string;
  /**
   * `network`: only the referral network (someone outside the practice), from the list's "Referral network" item;
   * `billing`: the practice's billing team on MedicAI Connect (2026-10-05)
   */
  mode: 'channel' | 'message' | 'network' | 'billing';
  /**
   * Billing mode from a money card's Chat button (the Connect page): the chat is about this patient. A group is named
   * after them, and with nobody picked the chat is the practice's billing room.
   */
  patient?: { title: string; treatingClinicalUserId?: string | null };
}

export interface NewConversationResult {
  conversationId: string;
}

interface Person {
  principalId: string;
  name: string;
  role: string;
  /** the claim's treating doctor (about a patient) */
  treating?: boolean;
}

/**
 * Starts a conversation in the selected practice.
 *   message   pick one person for a 1:1 (the existing one is reused), or 2–8 for a group message
 *   channel   a name, an optional description, public (anyone in the practice can find and join) or private
 *             (invitation only; practice admins still see it, O16), and its first members
 * Only people in this practice are listed (the hub refuses anyone else). The people picker is the task dialog's
 * "Assigned to" field (a multiple mat-select of the practice's members), after a roster sync so everyone is there.
 *
 * Referral network (2026-09-22): a message can also go 1-on-1 to someone OUTSIDE the practice it is linked with - a
 * linked MedicAI practice's doctor, or an external clinician. Listed from this app's server (the network links), and
 * opened with a ticket it signs; picking one opens the chat at once.
 *
 * Billing team (2026-10-05): one person on the practice's billing team on MedicAI Connect for a 1:1, or several (with
 * the practice's own people too, if wanted) for a group. Listed and opened through the hub, across the billing relation.
 */
@Component({
  selector: 'app-new-conversation-dialog',
  templateUrl: './new-conversation-dialog.component.html',
  styleUrls: ['./new-conversation-dialog.component.scss'],
})
export class NewConversationDialogComponent implements OnInit {
  people: Person[] = [];
  loading = true;
  busy = false;
  error = '';
  selectedIds: string[] = [];
  name = '';
  description = '';
  visibility: 'public' | 'private' = 'public';

  // Referral network (2026-09-22)
  readonly networkOn = REFERRALS_ENABLED;
  network: NetworkContactLink[] = [];
  networkLoading = false;
  networkError = '';
  networkQuery = '';
  /** the person whose chat is being opened (their row shows it) */
  openingKey: string | null = null;

  // Billing team (2026-10-05)
  billingTeams: BillingRelation[] = [];
  billingOrgId = '';
  groupName = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: NewConversationData,
    private readonly ref: MatDialogRef<NewConversationDialogComponent, NewConversationResult>,
    private readonly chat: ChatService,
    private readonly referrals: ReferralService,
  ) {}

  /** The network section shows for a new message and for the network-only item, when referrals are switched on; never in a billing chat about a patient. */
  get showNetwork(): boolean {
    return this.networkOn && this.data.mode !== 'channel' && !this.data.patient && !this.chat.isStateUnitOrg(this.data.orgId);
  }

  /** Links whose people match every word typed (name, speciality or practice); a link with nobody left is left out. */
  get networkShown(): NetworkContactLink[] {
    const words = normalise(this.networkQuery).split(' ').filter(Boolean);
    if (!words.length) return this.network.filter((l) => l.people.length);
    return this.network
      .map((l) => ({ ...l, people: l.people.filter((p) => {
        const hay = normalise([p.displayName, p.speciality, l.practiceName, p.email].filter(Boolean).join(' '));
        return words.every((w) => hay.includes(w));
      }) }))
      .filter((l) => l.people.length);
  }

  /** The line under a person's name: speciality, an external clinician's own practice. */
  personMeta(l: NetworkContactLink, p: NetworkContactPerson): string {
    return [p.speciality, l.kind === 'external' ? l.practiceName : null].filter(Boolean).join(' · ');
  }

  /**
   * Whether someone else in the list has this person's name. Their invite address is then shown in full strength, so
   * picking the wrong one of two "Dr J Smith"s is hard (2026-09-22: a chat was opened with the wrong one).
   */
  nameShared(p: NetworkContactPerson): boolean {
    const name = normalise(p.displayName);
    return this.network.reduce((n, l) => n + l.people.filter((q) => normalise(q.displayName) === name).length, 0) > 1;
  }

  personKey(l: NetworkContactLink, p: NetworkContactPerson): string {
    return `${l.linkId}:${p.userId ?? 'external'}`;
  }

  personHue(l: NetworkContactLink, p: NetworkContactPerson): number {
    return avatarHue(this.personKey(l, p));
  }

  trackLink(_i: number, l: NetworkContactLink): string {
    return l.linkId;
  }

  /** Opens the 1-on-1 with this person; the window shows it and this dialog closes. */
  async startNetwork(l: NetworkContactLink, p: NetworkContactPerson): Promise<void> {
    const groupId = this.chat.groupIdOfOrg(this.data.orgId);
    if (!groupId || this.openingKey) return;
    this.openingKey = this.personKey(l, p);
    this.error = '';
    try {
      const c = await this.chat.openNetworkChat(groupId, l.linkId, p.userId);
      this.ref.close({ conversationId: c.id });
    } catch (e) {
      this.error = e instanceof HubRequestError && e.code === 'NOT_FOUND'
        ? 'This person is no longer one of your linked medical professionals.'
        : 'Could not open the chat. Please try again.';
      this.openingKey = null;
    }
  }

  private async loadNetwork(): Promise<void> {
    const groupId = this.chat.groupIdOfOrg(this.data.orgId);
    if (!this.showNetwork || !groupId) return;
    this.networkLoading = true;
    try {
      this.network = await firstValueFrom(this.referrals.networkContacts(groupId));
    } catch {
      this.networkError = 'Could not load your medical professionals.';
    } finally {
      this.networkLoading = false;
    }
  }


  /** A person's photo in a list of people, or null when they have none (their initials are shown instead). */
  facePhoto(p: { principalId: string; avatar?: string }): string | null {
    return this.chat.store.avatarUrl(p.principalId, p.avatar ?? null);
  }

  faceInitials(p: { name: string }): string {
    return initialsOf(p.name);
  }

  faceHue(p: { principalId: string }): number {
    return avatarHue(p.principalId);
  }

  async ngOnInit(): Promise<void> {
    void this.loadNetwork();
    if (this.data.mode === 'network') {
      this.loading = false;
      return;
    }
    if (this.data.mode === 'billing') {
      await this.loadBilling();
      return;
    }
    try {
      // Everyone in the practice, not only people who have opened chat before.
      await this.chat.syncRoster().catch(() => undefined);
      const me = this.chat.store.snapshot.me?.principalId;
      this.people = (await this.chat.store.directory(this.data.orgId)).filter((p) => p.principalId !== me);
    } catch {
      this.error = 'Could not load the people in this practice.';
    } finally {
      this.loading = false;
    }
  }

  /** The practice's billing teams (usually one) and the people of the one picked. */
  private async loadBilling(): Promise<void> {
    try {
      this.billingTeams = (await this.chat.store.billingRelations()).filter((r) => r.orgId === this.data.orgId && !r.ended);
      this.billingOrgId = this.billingTeams[0]?.otherOrgId ?? '';
      await this.pickBillingTeam(this.billingOrgId);
    } catch {
      this.error = 'Could not load your billing team.';
      this.loading = false;
    }
  }

  async pickBillingTeam(otherOrgId: string): Promise<void> {
    this.billingOrgId = otherOrgId;
    this.selectedIds = [];
    this.people = [];
    if (!otherOrgId) {
      this.loading = false;
      return;
    }
    this.loading = true;
    try {
      const people = await this.chat.store.billingDirectory(this.data.orgId, otherOrgId);
      if (this.data.patient) {
        this.people = pickerPeople(people, this.data.patient.treatingClinicalUserId);
        this.selectedIds = this.people.filter((p) => p.treating).map((p) => p.principalId);
        this.groupName = patientGroupName(this.data.patient.title) ?? '';
      } else {
        this.people = people;
      }
    } catch {
      this.error = 'Could not load your billing team.';
    } finally {
      this.loading = false;
    }
  }

  /** The names of the people picked, for the select's closed text (the options also hold avatar initials). */
  get selectedNames(): string {
    return this.selectedIds.map((id) => this.people.find((p) => p.principalId === id)?.name ?? '').filter(Boolean).join(', ');
  }

  /** A message goes to at most eight others; once eight are chosen, the rest are disabled. */
  full(p: Person): boolean {
    return (this.data.mode === 'message' || this.data.mode === 'billing') && this.selectedIds.length >= 8 && !this.selectedIds.includes(p.principalId);
  }

  get canCreate(): boolean {
    if (this.busy) return false;
    if (this.data.mode === 'network') return false;
    if (this.data.mode === 'billing') return !!this.billingOrgId && (this.selectedIds.length > 0 || !!this.data.patient);
    return this.data.mode === 'channel' ? this.name.trim().length > 0 : this.selectedIds.length > 0;
  }

  async create(): Promise<void> {
    if (!this.canCreate) return;
    this.busy = true;
    this.error = '';
    try {
      const ids = [...this.selectedIds];
      const team = this.billingTeams.find((t) => t.otherOrgId === this.billingOrgId);
      if (this.data.patient && team) {
        this.ref.close({ conversationId: await this.chat.store.startBillingChat(team, ids, this.groupName.trim() || undefined) });
        return;
      }
      const c =
        this.data.mode === 'billing'
          ? ids.length === 1
            ? await this.chat.store.openBillingDirect(this.data.orgId, this.billingOrgId, ids[0] as string)
            : await this.chat.store.createBillingGroup(this.data.orgId, this.billingOrgId, ids, this.groupName.trim() || undefined)
          : this.data.mode === 'channel'
          ? await this.chat.store.createChannel({
              orgId: this.data.orgId,
              name: this.name.trim(),
              ...(this.description.trim() ? { description: this.description.trim() } : {}),
              visibility: this.visibility,
              memberIds: ids,
            })
          : ids.length === 1
            ? await this.chat.store.openDirect(this.data.orgId, ids[0] as string)
            : await this.chat.store.createGroup(this.data.orgId, ids);
      this.ref.close({ conversationId: c.id });
    } catch (e) {
      this.error = e instanceof HubRequestError && e.code === 'BAD_REQUEST' ? 'Check the details and try again.' : 'Could not create the conversation.';
      this.busy = false;
    }
  }
}
